import {computed, inject, Injectable, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  ArchitecturalPrinciple,
  ArchitecturalPrincipleDirectus,
  DirectusService,
  HrTool,
  HrToolFilters,
  parseArchitecturalPrinciple,
} from '@frontend/directus';
import {
  HrCatalogControllerService,
  HrToolProviderResponse,
  HrToolProviderTranslationResponse,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {HrCatalogReferenceService} from '../../shared/services/hr-catalog-reference.service';
import {ExtendedHrTool, HrToolProviderGroup, HrToolTile} from './hr-tool.types';
import {translate, TranslocoService} from '@jsverse/transloco';

/** The three system types a tool can be assigned to (SCRUM-304), in display order. */
export const APPLICATION_TYPES: readonly ArchitecturalPrinciple[] = [
  ArchitecturalPrinciple.SUITE_WITHOUT_PAYROLL,
  ArchitecturalPrinciple.SUITE_WITH_PAYROLL,
  ArchitecturalPrinciple.STANDALONE_SYSTEM,
];

const APPLICATION_TYPE_ICONS: Record<ArchitecturalPrinciple, string> = {
  [ArchitecturalPrinciple.SUITE_WITHOUT_PAYROLL]: 'pi pi-th-large',
  [ArchitecturalPrinciple.SUITE_WITH_PAYROLL]: 'pi pi-calculator',
  [ArchitecturalPrinciple.STANDALONE_SYSTEM]: 'pi pi-box',
};

/** Shown until the Directus architectural_principle rows (the label source) are loaded. */
const APPLICATION_TYPE_FALLBACK_LABELS: Record<ArchitecturalPrinciple, string> = {
  [ArchitecturalPrinciple.SUITE_WITHOUT_PAYROLL]: 'HR Suite without payroll accounting',
  [ArchitecturalPrinciple.SUITE_WITH_PAYROLL]: 'HR Suite with payroll accounting',
  [ArchitecturalPrinciple.STANDALONE_SYSTEM]: 'Standalone System',
};

/** The two catalog overviews: provider brand tiles vs. individual tool tiles (SCRUM-307). */
export type HrToolOverviewMode = 'providers' | 'tools';

/**
 * Fallback classification for tools without an assigned architectural principle: a tool counts
 * as an HR-Suite when its module functions span at least this many distinct HR module
 * categories (SCRUM-240 decision, kept only as the fallback since SCRUM-304).
 */
const SUITE_MODULE_THRESHOLD = 3;
/** Fallback when module_id is not populated on the tool's functions: raw function count. */
const SUITE_FUNCTION_FALLBACK = 10;

@Injectable({
  providedIn: 'root',
})
export class HrToolService {
  private readonly directusService = inject(DirectusService);
  private readonly catalogApi = inject(HrCatalogControllerService);
  private readonly referenceService = inject(HrCatalogReferenceService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);

  private toolsLoadInProgress = false;

  readonly hrTools = signal<ExtendedHrTool[]>([]);
  readonly aiCapabilityFacetTools = signal<HrTool[]>([]);
  readonly filters = signal<HrToolFilters>({});
  /** Backend provider brand metadata (logo, per-language brand text, founded year, website). */
  readonly providers = signal<HrToolProviderResponse[]>([]);
  /** Active UI language, so brand text (tagline/description/HQ) re-resolves on language switch. */
  readonly activeLang = signal<string>(this.transloco.getActiveLang());
  /** Client-side "Anwendungstyp" facet — empty set means "all". */
  readonly applicationTypeFilter = signal<Set<ArchitecturalPrinciple>>(new Set());
  /** Kept here so the chosen overview survives navigating to a tool detail page and back. */
  readonly overviewMode = signal<HrToolOverviewMode>('providers');
  private readonly comparisonTools = signal<HrTool[]>([]);
  /** Random rank per tool/provider, assigned once per page load so filter changes keep the order. */
  private readonly toolRanks = new Map<HrTool['id'], number>();
  private readonly providerRanks = new Map<string, number>();

  private readonly activeTranslocoLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  private principlesLoadInProgress = false;
  private readonly architecturalPrincipleRows = signal<ArchitecturalPrincipleDirectus[]>([]);

  /** Application-type labels resolved from the Directus architectural_principle collection —
   *  the same source the tool forms assign from, so filter chips and badges stay aligned. */
  private readonly applicationTypeLabels = computed<Record<ArchitecturalPrinciple, string>>(() => {
    const lang = this.activeTranslocoLang();
    const rows = this.architecturalPrincipleRows();
    const labels = {...APPLICATION_TYPE_FALLBACK_LABELS};
    APPLICATION_TYPES.forEach((type) => {
      const row = rows.find(
        (candidate) => candidate.principles.length === 1 && candidate.principles[0] === type,
      );
      const name =
        row?.translations?.find((translation) => translation.language === lang)?.name ??
        row?.translations?.find((translation) => !!translation.name)?.name;
      if (name) {
        labels[type] = name.trim();
      }
    });
    return labels;
  });

  ensureArchitecturalPrinciplesLoaded(): void {
    if (this.architecturalPrincipleRows().length > 0 || this.principlesLoadInProgress) {
      return;
    }
    this.principlesLoadInProgress = true;
    this.directusService.loadArchitecturalPrinciples().subscribe({
      next: (rows) => {
        this.architecturalPrincipleRows.set(rows);
        this.principlesLoadInProgress = false;
      },
      error: () => {
        this.principlesLoadInProgress = false;
      },
    });
  }

  applicationTypeLabel(type: ArchitecturalPrinciple): string {
    return this.applicationTypeLabels()[type];
  }

  applicationTypeIcon(type: ArchitecturalPrinciple): string {
    return APPLICATION_TYPE_ICONS[type];
  }

  /** The tool's assigned system type; tools without one fall back to the SCRUM-240 heuristic. */
  applicationTypeOf(tool: HrTool): ArchitecturalPrinciple {
    return (
      parseArchitecturalPrinciple(tool.architecturalPrinciple) ??
      (this.isSuiteByModuleCount(tool)
        ? ArchitecturalPrinciple.SUITE_WITHOUT_PAYROLL
        : ArchitecturalPrinciple.STANDALONE_SYSTEM)
    );
  }

  /** Tools grouped by provider brand, joined with backend metadata and the Anwendungstyp facet. */
  readonly providerGroups = computed<HrToolProviderGroup[]>(() => {
    const metaById = new Map(this.providers().map((provider) => [provider.id, provider]));
    const typeFilter = this.applicationTypeFilter();
    const lang = this.activeLang();

    const groupsById = new Map<string, HrToolProviderGroup>();
    this.hrTools().forEach((tool) => {
      const providerId = tool.toolProvider.id;
      const applicationType = this.applicationTypeOf(tool);
      const existing = groupsById.get(providerId);
      if (existing) {
        existing.tools.push(tool);
        if (!existing.applicationTypes.includes(applicationType)) {
          existing.applicationTypes.push(applicationType);
        }
        return;
      }
      const meta = metaById.get(providerId) ?? null;
      groupsById.set(providerId, {
        id: providerId,
        name: tool.toolProvider.name,
        meta,
        tagline: this.resolveProviderText(meta?.translations, lang, (t) => t.tagline),
        description: this.resolveProviderText(meta?.translations, lang, (t) => t.description),
        headquarters: this.resolveProviderText(meta?.translations, lang, (t) => t.headquarters),
        logoUrl: meta?.logoImage ? this.referenceService.imageUrl(meta.logoImage) : null,
        applicationTypes: [applicationType],
        tools: [tool],
      });
    });

    return Array.from(groupsById.values())
      .map((group) => ({
        ...group,
        applicationTypes: APPLICATION_TYPES.filter((type) => group.applicationTypes.includes(type)),
      }))
      .filter(
        (group) =>
          typeFilter.size === 0 || group.applicationTypes.some((type) => typeFilter.has(type)),
      )
      .sort(
        (first, second) =>
          this.rankOf(this.providerRanks, first.id) - this.rankOf(this.providerRanks, second.id),
      );
  });

  /** Individual tool tiles for the tools overview, honoring the Anwendungstyp facet. */
  readonly toolTiles = computed<HrToolTile[]>(() => {
    const typeFilter = this.applicationTypeFilter();
    const lang = this.activeLang();

    return this.hrTools()
      .map((tool) => ({
        id: tool.id,
        name: this.resolveToolName(tool, lang),
        providerName: tool.toolProvider.name,
        logoUrl: tool.coverImage || null,
        applicationType: this.applicationTypeOf(tool),
      }))
      .filter((tile) => typeFilter.size === 0 || typeFilter.has(tile.applicationType));
  });

  private resolveToolName(tool: ExtendedHrTool, lang: string): string {
    const translation = tool.translations.find((row) => row.language === lang);
    return translation?.name?.trim() ? translation.name : tool.name;
  }

  loadProviders(): void {
    this.catalogApi.getProviders().subscribe({
      next: (providers) => this.providers.set(providers),
    });
  }

  /**
   * Ensures the catalog tool list is populated. The catalog page normally loads it; this covers
   * opening/refreshing a tool detail page directly, where similar tools would otherwise be empty.
   */
  ensureToolsLoaded(): void {
    if (this.hrTools().length > 0 || this.toolsLoadInProgress) {
      return;
    }
    this.toolsLoadInProgress = true;
    this.directusService.loadHrTools({active: true}).subscribe({
      next: (tools) => {
        this.updateHrTools(tools);
        this.updateHrToolTranslations(this.transloco.getActiveLang());
        this.toolsLoadInProgress = false;
      },
      error: () => {
        this.toolsLoadInProgress = false;
      },
    });
  }

  /** Resets every facet — the sidebar filters and the client-side Anwendungstyp legend. */
  clearAllFilters(): void {
    this.filters.set({});
    this.applicationTypeFilter.set(new Set());
  }

  private isSuiteByModuleCount(tool: HrTool): boolean {
    const moduleIds = new Set(
      (tool.moduleFunctions ?? [])
        .map((moduleFunction) => moduleFunction.module_id?.id)
        .filter((id): id is number => id != null),
    );
    if (moduleIds.size > 0) {
      return moduleIds.size >= SUITE_MODULE_THRESHOLD;
    }
    return (tool.moduleFunctions?.length ?? 0) >= SUITE_FUNCTION_FALLBACK;
  }

  toggleApplicationType(type: ArchitecturalPrinciple): void {
    this.applicationTypeFilter.update((current) => {
      const next = new Set(current);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  updateHrTools(hrTools: HrTool[]) {
    const extendedTools = hrTools.map((item) => ({
      ...item,
      coverImage: item.coverImage ? this.directusService.getHrCatalogImageUrl(item.coverImage) : '',
      translatedName: '',
      translatedDescription: '',
      translatedDetailedDescription: '',
    }));
    extendedTools.forEach((tool) => {
      this.assignRandomRank(this.toolRanks, tool.id);
      this.assignRandomRank(this.providerRanks, tool.toolProvider.id);
    });
    this.hrTools.set(
      extendedTools.sort(
        (first, second) =>
          this.rankOf(this.toolRanks, first.id) - this.rankOf(this.toolRanks, second.id),
      ),
    );
  }

  private assignRandomRank<K>(ranks: Map<K, number>, key: K): void {
    if (!ranks.has(key)) {
      ranks.set(key, Math.random());
    }
  }

  private rankOf<K>(ranks: Map<K, number>, key: K): number {
    return ranks.get(key) ?? Number.MAX_SAFE_INTEGER;
  }

  /**
   * Picks a provider's brand text for the active language: the active-language value if present,
   * otherwise the first language that carries the field, mirroring the tool translation fallback.
   */
  private resolveProviderText(
    translations: HrToolProviderTranslationResponse[] | undefined,
    lang: string,
    pick: (translation: HrToolProviderTranslationResponse) => string | undefined,
  ): string | null {
    const rows = translations ?? [];
    const activeValue = pick(rows.find((row) => row.language === lang) ?? {language: lang})?.trim();
    if (activeValue) {
      return activeValue;
    }
    const fallback = rows.map(pick).find((value) => value?.trim());
    return fallback?.trim() ?? null;
  }

  updateHrToolTranslations(lang: string) {
    this.activeLang.set(lang);
    const hrTools = this.hrTools();
    hrTools.forEach((item) => {
      const translation = item.translations.find((translation) => translation.language === lang);
      if (translation) {
        item.translatedName =
          translation.name && translation.name.trim() !== '' ? translation.name : item.name;
        item.translatedDescription = translation.description;
        item.translatedDetailedDescription = translation.description_detailed;
      } else {
        item.translatedName = item.name;
      }
    });
    this.hrTools.set(hrTools);
  }

  readonly comparisonHrTools = computed(() => this.comparisonTools());

  addToolToComparison(tool: HrTool) {
    let currentTools = this.comparisonTools();
    if (currentTools.length < 3) {
      if (!currentTools.some((t) => t.id === tool.id)) {
        currentTools = [...currentTools, tool];
        this.comparisonTools.set(currentTools);
      } else {
        this.toast.showWarning(
          translate('warning.hrTools.comparison'),
          translate('warning.hrTools.compareSame'),
        );
      }
    } else {
      this.toast.showWarning(
        translate('warning.hrTools.comparison'),
        translate('warning.hrTools.compareMax'),
      );
    }
  }

  removeToolFromComparison(toolId: string) {
    const currentTools = this.comparisonTools().filter((tool) => tool.id !== toolId);
    this.comparisonTools.set(currentTools);
  }

  clearAllComparisonTools() {
    this.comparisonTools.set([]);
  }
}
